import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';
import { ServiceItem } from '../types';
import { allServices } from '../data/services';
import { API_ENDPOINTS } from '../config/api';

const UnitDetail: React.FC = () => {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguageStore();
  const [unit, setUnit] = useState<ServiceItem | null>(null);

  useEffect(() => {
    const fetchUnit = async () => {
      if (!unitId) return;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      try {
        // Try to fetch from API first
        const response = await fetch(API_ENDPOINTS.singleUnit(unitId), {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          setUnit(data.data);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (err: any) {
        clearTimeout(timeoutId);
        // Only log if it's not a connection error (backend not running)
        if (err.name !== 'AbortError' && !err.message?.includes('Failed to fetch')) {
          console.warn('Error fetching unit:', err);
        }
        // Fallback to local data
        const foundUnit = allServices.find(s => s.id === unitId);
        setUnit(foundUnit || null);
      }
    };

    fetchUnit();
  }, [unitId]);

  if (!unit) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-text-dark mb-4">
            {t('unit-not-found', 'Unit not found', 'ඒකකය හමු නොවීය')}
          </h2>
          <button
            onClick={() => navigate('/units')}
            className="btn-primary"
          >
            {t('back-to-units', 'Back to Units', 'ඒකක වෙත ආපසු')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <motion.button
          onClick={() => navigate('/units')}
          className="flex items-center gap-2 text-text-secondary hover:text-primary-green transition-colors mb-4 md:mb-6"
          whileHover={{ x: -5 }}
        >
          <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
          <span className="text-sm md:text-base">{t('back', 'Back', 'ආපසු')}</span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-xl"
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-text-dark mb-3 md:mb-4">
            {t(`service-${unit.id}-title`, unit.title, unit.titleSinhala)}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary mb-6 md:mb-8">
            {t(`service-${unit.id}-description`, unit.description, unit.descriptionSinhala)}
          </p>

          {unit.stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {Object.entries(unit.stats).map(([key, value]) => (
                <div key={key} className="bg-gray-50 rounded-lg p-3 md:p-4">
                  <h3 className="text-sm md:text-base font-semibold text-primary-green mb-1 md:mb-2">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </h3>
                  <p className="text-sm md:text-base text-text-dark whitespace-pre-line">
                    {typeof value === 'boolean' 
                      ? (value ? t('yes', 'Yes', 'ඔව්') : t('no', 'No', 'නැත'))
                      : String(value)
                    }
                  </p>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default UnitDetail;

